import { useEffect, useMemo, useRef, useState } from "react";
import { X, Camera, Sparkles } from "lucide-react";

/**
 * Saree photo picker with 4 guided sections — Border, Pallu, Body, Blouse —
 * so shop owners capture each part of the saree accurately.
 *
 * - On submit the parent receives a flattened `images: string[]` (canonical
 *   section order: border → pallu → body → blouse) AND `image_sections:
 *   string[]` aligned 1:1 with images so we can re-hydrate sections when
 *   editing later.
 * - Hydrate on edit: pass `initialImages` + optional `initialSections`.
 *   When sections are missing (legacy sarees) all photos land in "body".
 * - Photos auto-compressed by the parent's onPickFiles helper; we just take
 *   the data URLs as-is.
 */

const SECTIONS = [
    {
        key: "border",
        label: "Border",
        hint: "Close-up of the saree border — show the zari, weave and edge detail clearly.",
        tip: "Lay the border flat; one shot wide, one shot tight.",
    },
    {
        key: "pallu",
        label: "Pallu",
        hint: "The decorative pallu — drape it on a flat surface or model shoulder.",
        tip: "Capture motifs (peacock, mango, paisley) head-on.",
    },
    {
        key: "body",
        label: "Body",
        hint: "The full saree body — laid flat or on a hanger for the overall colour.",
        tip: "Daylight + neutral background works best.",
    },
    {
        key: "blouse",
        label: "Blouse",
        hint: "Matching blouse piece — front, back if printed, sleeve detail.",
        tip: "Place beside the saree for colour match.",
    },
];

export default function SareeSectionPicker({
    initialImages = [],
    initialSections = [],
    onChange,             // ({images: string[], image_sections: string[]}) => void
    compressFiles,        // async (FileList) => string[] of data URLs
    maxPerSection = 4,
}) {
    // Local state: a map of {sectionKey: [src, src, ...]}.
    // Initial fill: walk initialImages + initialSections in lockstep. If
    // `initialSections` is missing/shorter, leftover photos drop into "body".
    const initial = useMemo(() => {
        const groups = { border: [], pallu: [], body: [], blouse: [] };
        initialImages.forEach((src, i) => {
            const key = (initialSections[i] || "body").toLowerCase();
            (groups[key] || groups.body).push(src);
        });
        return groups;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [groups, setGroups] = useState(initial);
    const inputRefs = useRef({ border: null, pallu: null, body: null, blouse: null });

    // Bubble changes up — flatten in canonical section order.
    useEffect(() => {
        const images = [];
        const sections = [];
        SECTIONS.forEach((s) => {
            (groups[s.key] || []).forEach((src) => {
                images.push(src);
                sections.push(s.key);
            });
        });
        onChange?.({ images, image_sections: sections });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groups]);

    const totalCount = SECTIONS.reduce((n, s) => n + (groups[s.key]?.length || 0), 0);

    const onPick = async (sectionKey, e) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;
        try {
            const datas = await compressFiles(files);
            setGroups((g) => ({
                ...g,
                [sectionKey]: [...(g[sectionKey] || []), ...datas].slice(0, maxPerSection),
            }));
        } finally {
            // allow re-selecting the same file
            if (e.target) e.target.value = "";
        }
    };

    const removeAt = (sectionKey, idx) => {
        setGroups((g) => ({
            ...g,
            [sectionKey]: (g[sectionKey] || []).filter((_, i) => i !== idx),
        }));
    };

    return (
        <div className="space-y-4" data-testid="saree-section-picker">
            <div className="flex items-baseline justify-between">
                <div>
                    <label className="ayraa-overline">
                        Saree photos <span className="text-brand-bronze">*</span>
                    </label>
                    <p className="text-[11px] text-brand-soft mt-1">
                        Capture each part separately so customers (and our AI) see every detail.
                    </p>
                </div>
                <div className="font-mono text-[11px] text-brand-muted uppercase tracking-wider-2">
                    {totalCount} photo{totalCount === 1 ? "" : "s"}
                </div>
            </div>

            <div className="space-y-3">
                {SECTIONS.map((s, idx) => {
                    const items = groups[s.key] || [];
                    const filled = items.length > 0;
                    return (
                        <div
                            key={s.key}
                            className={`border rounded-sm p-3.5 transition-all ${
                                filled
                                    ? "border-brand-ink bg-brand-paper"
                                    : "border-brand-border bg-brand-subtle/40 hover:border-brand-bronze"
                            }`}
                            data-testid={`saree-section-${s.key}`}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="font-mono text-[10px] tracking-wider-2 bg-brand-ink text-white px-1.5 py-0.5 rounded-sm">
                                            0{idx + 1}
                                        </span>
                                        <span className="font-display text-lg tracking-editorial">
                                            {s.label}
                                        </span>
                                        {filled ? (
                                            <span className="ml-auto font-mono text-[10px] tracking-wider-2 text-emerald-700">
                                                ✓ {items.length}
                                            </span>
                                        ) : null}
                                    </div>
                                    <p className="text-xs text-brand-muted mt-1.5 leading-relaxed">
                                        {s.hint}
                                    </p>
                                    <p className="text-[11px] text-brand-soft mt-1 italic flex items-start gap-1">
                                        <Sparkles className="w-2.5 h-2.5 mt-1 text-brand-bronze shrink-0" strokeWidth={1.5} />
                                        {s.tip}
                                    </p>
                                </div>
                            </div>

                            {/* thumbs + add */}
                            <div className="grid grid-cols-5 gap-2 mt-3">
                                {items.map((src, i) => (
                                    <div key={i} className="relative aspect-square rounded-sm overflow-hidden border border-brand-border">
                                        <img src={src} alt={`${s.label}-${i}`} className="w-full h-full object-cover" />
                                        <button
                                            type="button"
                                            className="absolute top-0.5 right-0.5 bg-white/95 hover:bg-white rounded-full p-0.5 transition"
                                            onClick={() => removeAt(s.key, i)}
                                            aria-label={`Remove ${s.label} photo`}
                                            data-testid={`saree-section-${s.key}-remove-${i}`}
                                        >
                                            <X className="w-2.5 h-2.5" strokeWidth={2} />
                                        </button>
                                    </div>
                                ))}
                                {items.length < maxPerSection ? (
                                    <label
                                        className="aspect-square rounded-sm border border-dashed border-brand-border grid place-items-center cursor-pointer text-brand-muted hover:bg-white hover:border-brand-bronze hover:text-brand-bronze transition group"
                                        data-testid={`saree-section-${s.key}-add`}
                                    >
                                        <input
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            capture="environment"
                                            ref={(el) => { inputRefs.current[s.key] = el; }}
                                            className="hidden"
                                            onChange={(e) => onPick(s.key, e)}
                                        />
                                        <div className="flex flex-col items-center gap-0.5">
                                            <Camera className="w-3.5 h-3.5" strokeWidth={1.5} />
                                            <span className="text-[9px] font-mono uppercase tracking-wider-2">Add</span>
                                        </div>
                                    </label>
                                ) : null}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export { SECTIONS as SAREE_SECTIONS };
